import { z } from 'zod';
import { pickingStatusSchema } from '../commissioning/commissioning.schema';
import { stockReservationResponseSchema } from '../inventory/stock-movement.schema';
import {
  purchaseDocumentKindSchema,
  purchaseOrderStatusSchema,
} from '../procurement/purchase-order.schema';

/**
 * Belegfluss-Rück-Query zu einer Projekt-Materialposition (ProjectMaterialItem):
 * bündelt die über Soft-Refs verknüpften Belege — Bestellpositionen, aktive
 * Lager-Reservierungen und Kommissionierpositionen. Rein lesend, für die
 * Herkunfts-/Folgebeleg-Anzeige im Frontend.
 */

/** Verknüpfte Bestellposition (PurchaseOrderItem.materialItemId). */
export const purchaseOrderItemLinkSchema = z.object({
  orderId: z.string().uuid(),
  orderNumber: z.string(),
  orderKind: purchaseDocumentKindSchema,
  orderStatus: purchaseOrderStatusSchema,
  itemId: z.string().uuid(),
  articleId: z.string().uuid().nullable(),
  articleNumber: z.string().nullable(),
  description: z.string(),
  quantity: z.number(),
  /** Bereits eingegangene Menge (Teil-Eingang möglich). */
  receivedQty: z.number(),
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  /** Voraussichtlicher Liefertermin der Bestellung (Bestellkopf); null = nicht gepflegt. */
  expectedDate: z.string().datetime().nullable(),
});
export type PurchaseOrderItemLink = z.infer<typeof purchaseOrderItemLinkSchema>;

/** Verknüpfte Kommissionierposition (PickingItem.sourceMaterialItemId). */
export const pickingItemLinkSchema = z.object({
  orderId: z.string().uuid(),
  orderNumber: z.string(),
  orderStatus: pickingStatusSchema,
  itemId: z.string().uuid(),
  articleId: z.string().uuid().nullable(),
  articleNumber: z.string(),
  description: z.string(),
  requiredQty: z.number(),
  pickedQty: z.number(),
});
export type PickingItemLink = z.infer<typeof pickingItemLinkSchema>;

/** Alle Belege, die auf eine Materialposition verweisen (gebündelt). */
export const materialItemLinksResponseSchema = z.object({
  materialItemId: z.string().uuid(),
  /** Offene/erledigte Bestellpositionen dieser Position. */
  purchaseOrderItems: z.array(purchaseOrderItemLinkSchema),
  /** Aktive Lager-Reservierungen dieser Position. */
  reservations: z.array(stockReservationResponseSchema),
  /** Kommissionierpositionen (Snapshot der Materialliste). */
  pickingItems: z.array(pickingItemLinkSchema),
});
export type MaterialItemLinksResponse = z.infer<typeof materialItemLinksResponseSchema>;

/**
 * Bulk-Vorschau der verknüpften Bestellungen ALLER Materialpositionen eines
 * Projekts (Beleg-Spalte der Bedarfstabelle) — eine Zeile je Bestellposition,
 * in einer Abfrage statt N Einzel-Abfragen je Position (Effizienz).
 */
export const materialItemOrderLinkSchema = z.object({
  materialItemId: z.string().uuid(),
  orderId: z.string().uuid(),
  orderNumber: z.string(),
  /** Voraussichtlicher Liefertermin der Bestellung (Bestellkopf); null = nicht gepflegt. */
  expectedDate: z.string().datetime().nullable(),
});
export type MaterialItemOrderLink = z.infer<typeof materialItemOrderLinkSchema>;

/**
 * „im Bestellpool" an der Bedarfsposition (Tech-Lead 2026-09-29): Menge je
 * Materialposition, die in Pool-Bestellungen liegt (Bestellpool, ADR 0041) —
 * Anzeige „im Bestellpool · 5 m". Zählt bewusst NICHT als bestellt: `orderedQty`
 * bucht erst die Freigabe bzw. das Zusammenfassen (POOL → DRAFT). Enthält nur
 * Positionen mit Pool-Menge > 0; eine Zeile je Materialposition (summiert über
 * alle Pool-Bestellungen).
 */
export const materialItemPoolQuantitySchema = z.object({
  materialItemId: z.string().uuid(),
  /** Offene Menge in Pool-Bestellungen (Σ Menge − eingegangen), in der Einheit der Position. */
  pooledQty: z.number(),
});
export type MaterialItemPoolQuantity = z.infer<typeof materialItemPoolQuantitySchema>;
